// GENERATED by tools/updates/build.mjs — do not edit by hand.
// Re-run the generator to change anything here; hand edits are overwritten.
//
// WHAT THIS IS. The operator-facing record of what changed and when. The console
// is under rapid development against a live client, and a console that changes
// under someone without telling them is a console they stop trusting — the first
// time a panel moves and nobody said so, every later change looks like a fault.
//
// WHY IT IS GENERATED RATHER THAN WRITTEN. A changelog kept by hand is written
// last, when the work is done and the writer is tired, so it is the first thing
// dropped. The commits are already there and this project writes long ones with
// their reasoning intact, which is unusually good raw material. The generator
// turns them into operator language.
//
// WHY IT IS COMMITTED RATHER THAN POSTED. The generator drafts; it does not
// publish. Its output lands here as a file, goes through the same diff review as
// any other change, and reaches a client only on the next deploy. That is not
// timidity about automation — it is that every line here is a STATEMENT ABOUT
// THE PRODUCT made to a paying client, and a model summarising a commit can
// misread one. Cheap to check, expensive to get wrong.
//
// NO COMMIT HASHES REACH THIS FILE, AND THAT IS A RULE ABOUT THE BUNDLE, NOT
// ABOUT THE PAGE. This module is compiled into the frontend, so anything in it
// is readable in devtools whether or not it is rendered. Operators get operator
// language and nothing else — no shas, no file names, no branch or ticket
// numbers, no internal vocabulary.
//
// The audit trail still exists; it just lives where it belongs. The generator
// writes tools/updates/audit.json alongside this file, keyed by the same item
// titles, carrying the commits each claim came from. That is what a reviewer
// checks a generated sentence against before it ships, and it never leaves the
// repository.

/** What kind of change an entry describes, in the operator's terms. */
export type UpdateKind = "added" | "changed" | "fixed" | "data";

export interface UpdateItem {
  kind: UpdateKind;
  /** One line, plain language, what the operator can now see or do. */
  title: string;
  /** Optional detail — why it matters, or what to do differently. */
  body?: string;
}

export interface UpdateRelease {
  /** The date this went in front of operators, "YYYY-MM-DD". */
  date: string;
  items: UpdateItem[];
  /**
   * How many changes in this release had no effect an operator could see —
   * groundwork, tooling, tests. A COUNT and not a list: it is honest about the
   * release not being only what is shown, without spending the reader's
   * attention on work that changes nothing for them.
   */
  internalCount: number;
}

export const UPDATES: UpdateRelease[] = [
{
    "date": "2026-08-15",
    "items": [
      {
        "kind": "fixed",
        "title": "The monthly normal was describing each day's worst hour, not a typical one",
        "body": "The wind and wave ranges given for the month at a site were built from the strongest hour of each day, so they read high — a site shown as typically 10 to 14 knots is nearer 5 to 10 across all hours. Every hour on record now counts. The figure has also moved out of the location notes and into the window briefing, under Historic, with the range drawn as a line across the chart so today can be read against it."
      },
      {
        "kind": "fixed",
        "title": "Day labels on the timeline could overlap at short window lengths",
        "body": "Dates that did not have room to print are left out rather than drawn over one another."
      },
      {
        "kind": "fixed",
        "title": "The map's own controls could sit underneath the side panel",
        "body": "On a narrower window the side panel floats over the map rather than sitting beside it, and anything anchored to the map's left edge was hidden behind it. The search field and the conditions legend now sit inside the part of the map you can actually see."
      },
      {
        "kind": "added",
        "title": "Search the map for a port, a waypoint, a sea, a seabed feature or a position",
        "body": "A search field at the top left of the map. Ports come from the World Port Index — 3,669 harbours, including the vessels' own bases — and answer instantly with no connection. Seas, seabed features and national waters come from the marine gazetteer and follow a moment later; a country takes you to its offshore zone rather than to a point inland. Positions can be typed in any spelling: decimal, hemisphere letters, degrees and minutes, or degrees-minutes-seconds. Every result shows what kind of thing it is and where, so two places with the same name are told apart before you pick one."
      },
      {
        "kind": "added",
        "title": "The console works on a phone",
        "body": "Four surfaces — forecast, map, table and panels — become four tabs along the bottom, one on screen at a time with the whole display to itself, instead of a page two and a half screens long. The header carries the two marks and a menu button; everything else is one tap away."
      },
      {
        "kind": "added",
        "title": "Positions can be written the way your deliverable expects",
        "body": "Decimal degrees, degrees and decimal minutes, degrees-minutes-seconds, or UTM zone and easting/northing in metres. Whichever is chosen is used across the console and the report. The reference system is now stated wherever a position is printed — on the report's face and on the site map itself — so a figure lifted out of the document still says what its coordinates mean. All four describe the same point on WGS 84; no datum is changed by the setting."
      },
      {
        "kind": "added",
        "title": "The columns and the conditions table fold away",
        "body": "A grip on each divider folds the side panel or the forecast column; the conditions table folds from its own header. Folding the table or a column gives the map the space. A folded column leaves its grip behind on the divider, so it can always be brought back, and what you fold is remembered."
      },
      {
        "kind": "added",
        "title": "An Updates page",
        "body": "A page in the console listing what has changed and when, newest first, with corrections to figures placed before anything else."
      },
      {
        "kind": "data",
        "title": "The seasonal normal is thirty years, and says whether the site has got harder",
        "body": "The month's typical conditions are now drawn from thirty rolling years of reanalysis rather than a short recent window, and the two halves of that record are compared and reported rather than assumed equal. Where a site has changed, the briefing says so in the terms that matter to a job: the share of hours in that month exceeding the armed vessel and operation limit, then against now."
      },
      {
        "kind": "changed",
        "title": "Areas are now Waypoints, and a saved one is an exact position",
        "body": "The standing regions — Gulf of Mexico, Guyana-Suriname, Angola and the rest — are unchanged and still open the view onto that region. Anything you save is now the exact position you were on, and opens at whatever width you are currently viewing, rather than reimposing the width it was saved at. Waypoints saved before this keep the width they carried."
      },
      {
        "kind": "changed",
        "title": "The tropical banner is a cyclone symbol",
        "body": "It printed its whole state as a sentence across the top of the map, most days to say nothing had happened. The count of active storms stays on it and it turns red while any are up; the full wording is still there when you rest on it, and the storm list is one click away."
      },
      {
        "kind": "changed",
        "title": "The workability legend no longer takes a block of the map",
        "body": "The same five statements — layer, arrows, colour scale, time and source — laid along one strip at the foot of the map instead of a placard standing on it."
      },
      {
        "kind": "changed",
        "title": "Fold controls are visible, and in the same place on every panel",
        "body": "The chevron that folds a block was small enough to read as punctuation, and on the forecast tracks it sat in a different place from everywhere else. Every fold control is now a plate on the right-hand edge of its header, at a size you can aim at."
      },
      {
        "kind": "changed",
        "title": "Guide, Methods and Updates sit under one Docs menu",
        "body": "Four entries in the header instead of six. On a phone the menu opens as a list."
      }
    ],
    "internalCount": 7
  },
  {
    "date": "2026-08-15",
    "items": [
      {
        "kind": "fixed",
        "title": "Port depth: the console no longer says whether a vessel fits",
        "body": "It was comparing draft against whichever depth was deeper, and at nearly half the ports in the index that was the anchorage rather than the channel — Monrovia read 12.5 m when its channel is 7.9 m. The verdict has been withdrawn rather than patched. The World Port Index record is shown instead, with channel and anchorage stated separately, and the judgement is left to the master."
      },
      {
        "kind": "fixed",
        "title": "Wave period was comparing two different quantities",
        "body": "GFS-Waves publishes the peak period while ECMWF, Meteo-France and DWD publish the energy period. Those differ by roughly half again in a mixed sea, and all four were plotted on one axis as if comparable. Each model now declares which period it serves and the difference is shown rather than averaged away."
      },
      {
        "kind": "added",
        "title": "A printable operations report for any point and window",
        "body": "Verdict, site map, the three-hourly conditions grid, meteograms, the 0700 daily-progress line in the form's own six columns, and a whole-day weather summary. Print it, or download one self-contained file that opens on any laptop with no connection."
      },
      {
        "kind": "added",
        "title": "The report can be set to your conventions before it is generated",
        "body": "Times as the site's clock, UTC, or both. Heights in metres, feet, or both with one leading. The sound-velocity profile included or left out. Whatever is chosen is named on the face of the document, so a reader who was not there knows what they are looking at."
      },
      {
        "kind": "added",
        "title": "Wind and sea shown as sleeves flying from the picked point",
        "body": "Each flies the way the water is going, with length and colour carrying how much of your limit it uses. When the two are far enough apart the map says so — a wind across or against the sea is called out by name."
      },
      {
        "kind": "added",
        "title": "Conditions table with a number in every cell",
        "body": "Three-hourly across the window, grouped by day, each cell tinted by how much of the operating limit that number uses."
      },
      {
        "kind": "changed",
        "title": "The clock and units panel no longer sits open across the header",
        "body": "It is behind the gear, where it was always meant to be."
      }
    ],
    "internalCount": 48
  }
];
